import type {
  AgregadoCuestionario,
  BateriaEvaluacionEstado,
  BateriaEvaluacionFase,
  BateriaForma,
  BateriaPaquete,
  BateriaPaqueteEstado,
  IngestaJob,
  NivelDistribucion,
} from '@/hooks/bateria/use-bateria'
import type {FormaResumen, NivelResumen} from '@/hooks/bateria/use-bateria-informe'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import {normalize} from '@/lib/utils'

/** El OMR de una página tarda segundos: diez minutos sin ningún desenlace es un lote detenido. Se mide con
 * el reloj del navegador; uno adelantado solo desbloquea los controles antes de tiempo. */
export const LOTE_SIN_AVANCE_MS = 10 * 60 * 1000

/** `enBlanco` son reversos vacíos que el sistema descartó solo: se nombran para no decidir a espaldas de
 * nadie. `porProcesar` son las páginas sin desenlace de ningún tipo. */
export type LotesResumen = {
  lotes: number
  paginasDelArchivo: number
  leidas: number
  sinUbicar: number
  conError: number
  enBlanco: number
  porProcesar: number
  /** Hay al menos un lote en `enumerando`/`procesando`. */
  corriendo: boolean
  /** Corriendo, pero NINGUNO de los activos avanzó en los últimos `LOTE_SIN_AVANCE_MS`. */
  detenido: boolean
}

export function resumirLotes(jobs: IngestaJob[], ahora: number): LotesResumen {
  const suma = (pick: (job: IngestaJob) => number) => jobs.reduce((total, job) => total + pick(job), 0)
  const paginasDelArchivo = suma((job) => job.paginas_total)
  const leidas = suma((job) => job.paginas_hechas)
  const sinUbicar = suma((job) => job.paginas_en_cuarentena)
  const conError = suma((job) => job.paginas_fallidas)
  const enBlanco = suma((job) => job.paginas_en_blanco)
  const activos = jobs.filter((job) => job.estado === 'enumerando' || job.estado === 'procesando')
  return {
    lotes: jobs.length,
    paginasDelArchivo,
    leidas,
    sinUbicar,
    conError,
    enBlanco,
    // Tareas paralelas suben los contadores y `paginas_total` se sella al enumerar: un instante suma de más.
    porProcesar: Math.max(0, paginasDelArchivo - leidas - sinUbicar - conError - enBlanco),
    corriendo: activos.length > 0,
    detenido: activos.length > 0 && activos.every((job) => ahora - Date.parse(job.updated_at) > LOTE_SIN_AVANCE_MS),
  }
}

/** Espejo de `paginas_sin_desenlace` (`core/bateria_asentamiento.py`), piso en 0 incluido. */
export function paginasSinDesenlace(job: IngestaJob): number {
  return Math.max(
    0,
    job.paginas_total -
      job.paginas_hechas -
      job.paginas_fallidas -
      job.paginas_en_cuarentena -
      job.paginas_duplicadas -
      job.paginas_en_blanco
  )
}

/** El servidor exige lo mismo antes de asentarla (409 si no): «Descartar» no aparece donde fallaría. */
export function tandaDetenida(job: IngestaJob, ahora: number): boolean {
  const activa = job.estado === 'enumerando' || job.estado === 'procesando'
  return activa && ahora - Date.parse(job.updated_at) > LOTE_SIN_AVANCE_MS
}

export function silencioDeLotes(jobs: IngestaJob[], ahora: number): number {
  const activos = jobs.filter((job) => job.estado === 'enumerando' || job.estado === 'procesando')
  if (activos.length === 0) return 0
  return Math.max(0, ahora - Math.max(...activos.map((job) => Date.parse(job.updated_at))))
}

/** Antes de esto la tasa es la enumeración y el arranque: una cifra que se desploma sola enseña a no creerle. */
export const ETA_MINIMO_MS = 15 * 1000

/** Marcas estables de las excepciones del motor en `job.error` (inglés técnico, con los topes interpolados).
 * Lo que no calce cae al genérico: el texto crudo nunca se muestra. */
const MOTIVOS_DE_LOTE: [RegExp, string][] = [
  [/more than \d+ pages/i, 'demasiadasPaginas'],
  [/more than \d+ paquetes/i, 'demasiadasPersonas'],
  [/too large uncompressed/i, 'demasiadoGrande'],
  [/too many entries/i, 'demasiadasEntradas'],
  [/suspicious compression ratio/i, 'archivoSospechoso'],
  [/nests deeper than/i, 'demasiadosNiveles'],
]

export function claveDeErrorDeLote(error: string): string {
  const encontrado = MOTIVOS_DE_LOTE.find(([patron]) => patron.test(error))
  return `admin.bateria.escaneosV2.historial.motivo.${encontrado ? encontrado[1] : 'generico'}`
}

export function paginasConDesenlace(job: IngestaJob): number {
  const suma = job.paginas_hechas + job.paginas_fallidas + job.paginas_en_cuarentena + job.paginas_en_blanco
  // Techo para lotes contados de más: un reintento de la misma página podía contarla dos veces.
  return job.paginas_total > 0 ? Math.min(job.paginas_total, suma) : suma
}

/** Con la tasa observada del propio lote. `null` = todavía no se puede decir (sin total, sin historia o
 * sin páginas resueltas) y la pantalla dice «calculando…». */
export function faltaDelLote(job: IngestaJob, ahora: number): number | null {
  if (job.paginas_total <= 0) return null
  const transcurrido = ahora - Date.parse(job.created_at)
  if (transcurrido < ETA_MINIMO_MS) return null
  const hechas = paginasConDesenlace(job)
  if (hechas <= 0) return null
  const restantes = job.paginas_total - hechas
  if (restantes <= 0) return 0
  return Math.round((transcurrido / hechas) * restantes)
}

/** El máximo sobre las tandas activas, que van en paralelo. Si una sola no sabe todavía, `null`: ignorarla
 * haría que la cifra se desplomara sola. */
export function faltaDelLoteAgregada(jobs: IngestaJob[], ahora: number): number | null {
  const activos = jobs.filter((job) => job.estado === 'enumerando' || job.estado === 'procesando')
  if (activos.length === 0) return null
  let mayor = 0
  for (const job of activos) {
    const falta = faltaDelLote(job, ahora)
    if (falta === null) return null
    if (falta > mayor) mayor = falta
  }
  return mayor
}

/** No afirma «su escaneo falló»: con el manifiesto vacío el lote no guarda de qué paquete era cada archivo. */
export type PaqueteSinPaginas = 'por_escanear' | 'esperando_identificacion' | 'sin_hoja_identificada'

export function estadoSinPaginas(paquete: BateriaPaquete, lotes: LotesResumen): PaqueteSinPaginas | null {
  if (paquete.estado !== 'generado') return null
  // Un lote que abortó antes de enumerar no dejó páginas: ahí sí sigue faltando escanear.
  const recibioEscaneos = lotes.paginasDelArchivo > 0 || lotes.corriendo
  if (!recibioEscaneos) return 'por_escanear'
  return lotes.corriendo && !lotes.detenido ? 'esperando_identificacion' : 'sin_hoja_identificada'
}

/** Bloqueado (páginas en cuarentena o sin ensamblar), no lento. La celda y el filtro comparten este predicado. */
export function esIncompleto(paquete: BateriaPaquete, batchRunning: boolean): boolean {
  return paquete.estado === 'transcribiendo' && paquete.paginas_faltantes.length > 0 && !batchRunning
}

/** Mide con `pendientes_de_decision`, nunca con las banderas crudas del motor, que no bajan al confirmar.
 * La celda, el filtro y el botón primario comparten esta definición. */
export function esListoParaValidar(paquete: BateriaPaquete): boolean {
  return paquete.estado === 'transcrito' && paquete.pendientes_de_decision === 0
}

// Solo los cuatro que el backend asigna: `escaneado`, `revisado` y `error` no se asignan en ningún sitio.
export const ESTADO_ORDER: BateriaPaqueteEstado[] = ['generado', 'transcribiendo', 'transcrito', 'validado']

// Estados derivados, no del enum: viajan como centinelas para que filtro y celda pinten el mismo conjunto.
export const INCOMPLETO_FILTER = 'incompleto'
export const LISTO_FILTER = 'listo_para_validar'
export type EstadoFilter = BateriaPaqueteEstado | typeof INCOMPLETO_FILTER | typeof LISTO_FILTER | ''

/** `transcrito` deja afuera lo listo para validar: son dos rótulos distintos en la tabla. */
export function coincideEstado(paquete: BateriaPaquete, filtro: EstadoFilter, batchRunning: boolean): boolean {
  if (!filtro) return true
  if (filtro === INCOMPLETO_FILTER) return esIncompleto(paquete, batchRunning)
  if (filtro === LISTO_FILTER) return esListoParaValidar(paquete)
  if (filtro === 'transcrito') return paquete.estado === 'transcrito' && !esListoParaValidar(paquete)
  return paquete.estado === filtro
}

// En tránsito es `enCurso` y no lime: el lime es el escalón bajo de la rampa de magnitud (U2).
export const ESTADO_TONE: Record<BateriaPaqueteEstado, 'neutral' | 'enCurso' | 'amber' | 'green' | 'ink' | 'red'> = {
  generado: 'neutral',
  escaneado: 'enCurso',
  transcribiendo: 'enCurso',
  transcrito: 'amber',
  revisado: 'green',
  validado: 'ink',
  error: 'red',
}

export const EVALUACION_ESTADO_TONE: Record<BateriaEvaluacionEstado, 'green' | 'ink' | 'neutral'> = {
  abierta: 'green',
  cerrada: 'ink',
  entregada: 'neutral',
}

/** Orden del enum `BateriaEvaluacionFase`; la fase actual siempre la manda el servidor. */
export const ORDEN_FASES: BateriaEvaluacionFase[] = [
  'roster',
  'campo',
  'lectura',
  'revision',
  'listo_para_informe',
  'informe',
]

// Una fase es identidad, no magnitud (U2): todas neutras salvo la terminal.
export const FASE_TONE: Record<BateriaEvaluacionFase, 'neutral' | 'ink'> = {
  roster: 'neutral',
  campo: 'neutral',
  lectura: 'neutral',
  revision: 'neutral',
  listo_para_informe: 'neutral',
  informe: 'ink',
}

export const NIVEL_TONE: Record<string, 'green' | 'amber' | 'orange' | 'red'> = {
  sin_riesgo: 'green',
  bajo: 'green',
  medio: 'amber',
  alto: 'orange',
  muy_alto: 'red',
}

// Orden ascendente, igual que NIVELES del backend.
export const NIVELES = ['sin_riesgo', 'bajo', 'medio', 'alto', 'muy_alto'] as const

const SEVERIDAD = ['muy_alto', 'alto', 'medio', 'bajo', 'sin_riesgo'] as const

export function dominantNivel(dist: NivelDistribucion): string | null {
  return SEVERIDAD.find((n) => (dist.distribucion[n] ?? 0) > 0) ?? null
}

export function severidadRank(dist: NivelDistribucion): number {
  const idx = SEVERIDAD.findIndex((n) => (dist.distribucion[n] ?? 0) > 0)
  return idx === -1 ? SEVERIDAD.length : idx
}

export function totalPersonas(dist: NivelDistribucion): number {
  return NIVELES.reduce((sum, n) => sum + (dist.distribucion[n] ?? 0), 0)
}

export function enRiesgo(dist: NivelDistribucion): number {
  return (dist.distribucion.alto ?? 0) + (dist.distribucion.muy_alto ?? 0)
}

export function enRiesgoPct(dist: NivelDistribucion): number {
  const total = totalPersonas(dist)
  return total === 0 ? 0 : Math.round((enRiesgo(dist) / total) * 100)
}

export function dimensionesEnRojo(agregado: AgregadoCuestionario): number {
  return Object.values(agregado.dimensiones).filter((d) => {
    const nivel = dominantNivel(d)
    return nivel === 'alto' || nivel === 'muy_alto'
  }).length
}

/** Para un `nivel` que ya derivó el servidor (`reporte-datos`), nunca re-derivado de una distribución. */
const SEVERIDAD_NIVEL = ['muy_alto', 'alto', 'medio', 'bajo', 'sin_riesgo'] as const
export function severidadRankNivel(nivel: string | null): number {
  const idx = SEVERIDAD_NIVEL.findIndex((n) => n === nivel)
  return idx === -1 ? SEVERIDAD_NIVEL.length : idx
}

/** `reporte-datos` y `/agregados` serializan el mismo agregado de dos formas; esto adapta, no recalcula. */
export function agregadoDeFormaResumen(forma: FormaResumen): AgregadoCuestionario {
  const dist = (n: NivelResumen | null): NivelDistribucion => ({
    distribucion: n?.distribucion ?? {},
    n_validos: n?.n_validos ?? 0,
    promedio_bruto: null,
    promedio_transformado: n?.promedio_transformado ?? null,
  })
  const porClave = (lista: NivelResumen[]): Record<string, NivelDistribucion> =>
    Object.fromEntries(lista.map((n) => [n.clave, dist(n)]))
  return {
    cuestionarios: porClave(forma.cuestionarios),
    dominios: porClave(forma.dominios),
    dimensiones: porClave(forma.dimensiones),
    total_general: dist(forma.total_general),
  }
}

// Igual que `bateria_reporte_charts._MAX_CATEGORIAS_VERTICAL`: pantalla y Word rotan en el mismo punto.
export const MAX_CATEGORIAS_VERTICAL = 14

/** Nombres públicos de la taxonomía del manual, no texto de ítems (con derechos de autor). */
export function prettifyTaxonomia(key: string): string {
  const spaced = key.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

// Solo la escala de respuesta, nunca el texto del ítem (derechos de autor).
const SCALE_LIKERT5 = ['siempre', 'casi_siempre', 'algunas_veces', 'casi_nunca', 'nunca'] as const
const SCALE_ESTRES4 = ['siempre', 'casi_siempre', 'a_veces', 'nunca'] as const
const SCALE_FILTRO = ['si', 'no'] as const

/** `fila` es la banda que decidió las filas al leer (la única alineable), `ventana` una vecindad, `pagina` la hoja. */
export type ModoRecorte = 'fila' | 'ventana' | 'pagina'

/** «En blanco» (atajo `0`, docs/modulos/bateria-confirmacion-spec.md §3.6): una salida de la interfaz,
 * no una casilla del recorte. */
export const SIN_RESPUESTA = 'sin_respuesta'

export function scaleForItem(itemId: string): readonly string[] {
  if (itemId.startsWith('estres_')) return SCALE_ESTRES4
  if (itemId.startsWith('filtro_')) return SCALE_FILTRO
  return SCALE_LIKERT5
}

export function cuestionarioForItem(itemId: string): 'intralaboral' | 'extralaboral' | 'estres' | 'filtro' | 'otro' {
  if (itemId.startsWith('intra_')) return 'intralaboral'
  if (itemId.startsWith('extra_')) return 'extralaboral'
  if (itemId.startsWith('estres_')) return 'estres'
  if (itemId.startsWith('filtro_')) return 'filtro'
  return 'otro'
}

const FORMA_A_KEYWORDS = [
  'jefe',
  'jefa',
  'director',
  'directora',
  'gerente',
  'coordinador',
  'coordinadora',
  'profesional',
  'analista',
  'ingenier',
  'tecnic',
  'técnic',
  'supervisor',
  'especialista',
  'lider',
  'líder',
]
const FORMA_B_KEYWORDS = [
  'auxiliar',
  'operario',
  'operaria',
  'operador',
  'operadora',
  'asistente',
  'ayudante',
  'obrero',
  'servicios generales',
  'mensajero',
  'conductor',
]

/** Criterio de la Res. 2764 como sugerencia por cargo: jefaturas y profesionales forma A, auxiliares y
 * operarios forma B; siempre editable. */
export function suggestForma(employee: Employee, fallback: BateriaForma): BateriaForma {
  const cargos = employee.positions.map((p) => normalize(p.name)).join(' ')
  if (FORMA_B_KEYWORDS.some((k) => cargos.includes(k))) return 'B'
  if (FORMA_A_KEYWORDS.some((k) => cargos.includes(k))) return 'A'
  return fallback
}

export const ZIP_TYPES = new Set(['application/zip', 'application/x-zip-compressed'])
export const PDF_TYPE = 'application/pdf'
// Espejo de MAX_SCAN_BYTES del backend: 2 GiB por subida, ~500 baterías.
export const MAX_SCAN_BYTES = 2 * 1024 * 1024 * 1024

function isZipByName(file: File): boolean {
  return file.name.toLowerCase().endsWith('.zip')
}

/** El `type` puede venir vacío (drag-and-drop) o como `application/x-zip-compressed`: la extensión es el respaldo. */
export function isZipFile(file: File): boolean {
  return ZIP_TYPES.has(file.type) || isZipByName(file)
}

function isPdfFile(file: File): boolean {
  return file.type === PDF_TYPE || file.name.toLowerCase().endsWith('.pdf')
}

/** Devuelve la clave i18n: cada llamador decide su copy. */
export function scanFileError(file: File): 'tooBig' | 'badType' | null {
  if (file.size > MAX_SCAN_BYTES) return 'tooBig'
  if (!isZipFile(file) && !isPdfFile(file)) return 'badType'
  return null
}

/** Sin el prefijo `{uuid}_` de `scan_object_key`; si no calza, el tramo completo. */
export function displayFilename(objectKey: string): string {
  const leaf = objectKey.slice(objectKey.lastIndexOf('/') + 1)
  return leaf.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i, '')
}
